import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  useScrollTrigger,
  Slide,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useThemeMode } from '../context/ThemeContext';
import companyLogo from '../images/logo.png';

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Products', href: '#products' },
  { label: 'Industries', href: '#industries' },
  { label: 'Contact', href: '#contact' },
];

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { toggleTheme, isDark } = useThemeMode();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <Box
        sx={{
          bgcolor: isDark ? '#0f0f0f' : '#f5f5f5',
          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
          py: 1,
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.1)',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 16, color: '#e57373' }} />
                <Typography variant="caption" sx={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 600, fontSize: '0.85rem' }}>
                  +966 13 834 2170 | +966 55 540 5414 | +966 58 343 0459
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: 16, color: '#e57373' }} />
              <Typography variant="caption" sx={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 600, fontSize: '0.85rem' }}>
                info@alamoudiest.com.sa
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Navbar */}
      <HideOnScroll>
        <AppBar
          position="sticky"
          sx={{
            bgcolor: scrolled 
              ? (isDark ? 'rgba(0,0,0,0.95)' : 'rgba(255,255,255,0.95)') 
              : (isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)'),
            backdropFilter: 'blur(10px)',
            color: isDark ? 'white' : 'text.primary',
            boxShadow: scrolled 
              ? (isDark ? '0 4px 30px rgba(0,0,0,0.5)' : '0 4px 30px rgba(0,0,0,0.1)') 
              : 'none',
            borderBottom: '1px solid',
            borderColor: scrolled ? 'rgba(229, 115, 115, 0.3)' : 'transparent',
            transition: 'all 0.3s ease',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ py: 1 }}>
              {/* Logo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexGrow: 1,
                  cursor: 'pointer',
                }}
                onClick={() => scrollToSection('#home')}
              >
                <Box
                  component="img"
                  src={companyLogo}
                  alt="Al Amoudi Bearing Logo"
                  sx={{
                    height: 50,
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Orbitron", sans-serif',
                      fontWeight: 700,
                      color: isDark ? 'white' : 'text.primary',
                      lineHeight: 1.1,
                      fontSize: '1.1rem',
                      letterSpacing: 1,
                    }}
                  >
                    MOHAMMED SAEED AL AMOUDI
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: '#e57373', 
                      letterSpacing: 3, 
                      fontSize: '0.65rem', 
                      display: 'block',
                      fontFamily: '"Rajdhani", sans-serif',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}
                  >
                    Trading Establishment
                  </Typography>
                </Box>
              </Box>

              {/* Desktop Navigation */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={{
                      color: isDark ? 'white' : 'text.primary',
                      fontWeight: 600,
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1rem',
                      px: 2,
                      letterSpacing: 1,
                      '&:hover': {
                        color: '#e57373',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                
                {/* Dark Mode Toggle */}
                <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'}>
                  <IconButton
                    onClick={toggleTheme}
                    sx={{
                      ml: 1,
                      color: isDark ? 'white' : 'text.primary',
                      border: isDark ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        borderColor: '#e57373',
                      },
                    }}
                  >
                    {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  onClick={() => scrollToSection('#contact')}
                  endIcon={<KeyboardArrowRightIcon />}
                  sx={{ 
                    ml: 3,
                    borderColor: '#e57373',
                    color: '#e57373',
                    fontFamily: '"Rajdhani", sans-serif',
                    fontWeight: 700,
                    borderRadius: 0,
                    px: 3,
                    '&:hover': {
                      borderColor: '#e57373',
                      bgcolor: '#e57373',
                      color: 'white',
                    }
                  }}
                >
                  Get Quote
                </Button>
              </Box>

              {/* Mobile Menu Button */}
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: 320,
            bgcolor: isDark ? '#0f0f0f' : 'background.paper',
            color: isDark ? 'white' : 'text.primary',
            borderLeft: isDark ? '1px solid #333' : '1px solid #eee',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: '"Orbitron", sans-serif', color: '#e57373' }}>
              MENU
            </Typography>
            <IconButton onClick={handleDrawerToggle} sx={{ color: isDark ? 'white' : 'text.primary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItem
                button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                sx={{
                  borderRadius: 0,
                  mb: 1,
                  borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                  '&:hover': {
                    bgcolor: 'rgba(229, 115, 115, 0.1)',
                    pl: 3,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{
                    fontFamily: '"Rajdhani", sans-serif',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 2,
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => scrollToSection('#contact')}
              sx={{ 
                bgcolor: '#e57373',
                color: 'white',
                fontFamily: '"Rajdhani", sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                py: 1.5,
                '&:hover': {
                  bgcolor: '#d32f2f',
                }
              }}
            >
              Get A Quote
            </Button>
          </Box>

          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.5)', mb: 1, fontFamily: '"Rajdhani", sans-serif' }}>
              CONTACT INFO
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: '#e57373' }} />
              <Typography variant="body2" sx={{ fontFamily: '"Rajdhani", sans-serif' }}>
                +966 58 343 0459
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmailIcon sx={{ fontSize: 18, color: '#e57373' }} />
              <Typography variant="body2" sx={{ fontFamily: '"Rajdhani", sans-serif' }}>
                info@alamoudiest.com.sa
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
